import {
  ApolloClient,
  ApolloLink,
  ApolloProvider as RawApolloProvider,
  HttpLink,
  InMemoryCache,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { SentryLink } from 'apollo-link-sentry';
import { ReactNode, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { TypedTypePolicies } from '~graphql/__generated__/apollo-helpers';

interface Props {
  uri: string;
  children: ReactNode;
}

function ApolloProvider({ uri, children }: Props) {
  const toast = useToast();
  const navigate = useNavigate();

  const defaultLink = useMemo(() => {
    return setContext(async () => {
      return {};
    });
  }, []);

  const errorLink = onError(({ graphQLErrors }) => {
    const hasUnauthenticatedError = graphQLErrors?.some((err) => err.message === 'UNAUTHORIZED');

    if (hasUnauthenticatedError) {
      if (window.location.pathname === '/') {
        navigate('/403', { replace: true });
      }

      toast({
        status: 'error',
        title: 'You are not authorized to perform this operation',
      });
    }
  });

  const httpLink = useMemo(() => {
    return new HttpLink({
      uri,
    });
  }, [uri]);

  const retryLink = useMemo(() => {
    return new RetryLink();
  }, []);

  const sentryLink = useMemo(() => {
    return new SentryLink({
      uri: process.env.REACT_APP_API_URI,
      setTransaction: true,
      setFingerprint: true,
      attachBreadcrumbs: {
        includeQuery: true,
        includeFetchResult: false,
        includeError: true,
        includeCache: false,
        transform: undefined,
      },
    });
  }, []);

  const responseLink = new ApolloLink((operation, forward) => {
    return forward(operation).map((response) => {
      const context = operation.getContext();
      const headers = context.response.headers;

      const AmznTraceId: string = headers.get('X-Amzn-Trace-Id');

      Sentry.setContext('AWS', {
        traceId: AmznTraceId,
      });

      return response;
    });
  });

  const policies = useMemo<TypedTypePolicies>(
    () => ({
      Query: {},

      Mutation: {
        fields: {},
      },
    }),
    [],
  );

  const client = useMemo(() => {
    return new ApolloClient({
      cache: new InMemoryCache({
        typePolicies: policies,
      }),
      defaultOptions: {
        watchQuery: {
          fetchPolicy: 'network-only',
          nextFetchPolicy: 'cache-first',
        },
      },
      link: defaultLink
        .concat(sentryLink)
        .concat(errorLink)
        .concat(retryLink)
        .concat(responseLink)
        .concat(httpLink),
      connectToDevTools: process.env.REACT_APP_DEPLOY_ENVIRONMENT !== 'production',
    });
    // ErrorLink is excluded from the dependencies to prevent re-rendering.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultLink, retryLink, httpLink, policies, sentryLink]);

  return <RawApolloProvider client={client}>{children}</RawApolloProvider>;
}

export default ApolloProvider;
